import {ReactNode, createContext, useContext, useMemo} from 'react';
import {useSuspenseApiQuery} from '@legit.health/backend-client';
import {generatePath, generateRandomId} from '@legit.health/ui';
import * as Sentry from '@sentry/react';
import Company from '@/interfaces/Company';
import postMessageToParent from '@/utils/postMessageToParent';

export type MacroscopicMediaParam = 'disabled' | 'optional' | 'required';
const ParamsContext = createContext<{
  companyApiKey: string;
  companyCallbackUrl: string | null;
  extraData: string | null;
  isForPatient: boolean;
  showQuestionnairesHeader: boolean;
  enableAlternativeCameraModule: boolean;
  enableAlternativeCameraModuleAndroid: boolean;
  enableAnamnesis: boolean;
  enableExtendedInstructions: boolean;
  enableResult: boolean;
  enableQrUpload: boolean;
  macroscopicMedia: MacroscopicMediaParam;
  visitIdentifier: string;
}>({
  companyApiKey: '',
  companyCallbackUrl: '',
  extraData: '',
  isForPatient: false,
  showQuestionnairesHeader: true,
  enableAlternativeCameraModule: false,
  enableAlternativeCameraModuleAndroid: false,
  enableAnamnesis: false,
  enableExtendedInstructions: false,
  enableResult: false,
  enableQrUpload: false,
  macroscopicMedia: 'disabled',
  visitIdentifier: generateRandomId(128)
});

const params = new URLSearchParams(window.location.search);
const enableCamera = params.get('enableCamera');
const enableCameraForAndroid = params.get('enableCameraForAndroid');
const enableAlternativeCameraModule = params.get('enableAlternativeCameraModule');
const enableAlternativeCameraModuleAndroid = params.get('enableAlternativeCameraModuleAndroid');
const companyApiKey = params.get('company');
const extraData = params.get('extraData');
const companyCallbackUrl = params.get('companyCallbackUrl');
const isForPatient = params.get('isForPatient');
const showQuestionnairesHeader = params.get('showQuestionnairesHeader') ?? '1';
const enableAnamnesis = params.get('enableAnamnesis') ?? '0';
const enableExtendedInstructions = params.get('enableExtendedInstructions') ?? '0';
const isResultEnabled = params.get('isResultEnabled');
const enableResult = params.get('enableResult');
const enableQrUpload = params.get('enableQrUpload');
const visitIdentifier = params.get('visitIdentifier') ?? generateRandomId(128);

let macroscopicMedia = params.get('macroscopicMedia') ?? '';
const validMacroscopicMediaValues = ['disabled', 'optional', 'required'];
if (!validMacroscopicMediaValues.includes(macroscopicMedia)) {
  macroscopicMedia = 'disabled';
}

Sentry.setUser({id: companyApiKey ?? ''});
Sentry.setContext('app', {
  enableCamera,
  enableCameraForAndroid,
  companyApiKey,
  extraData,
  companyCallbackUrl,
  isForPatient,
  enableAnamnesis
});

export function ParamsContextProvider({children}: {children: ReactNode}) {
  if (!companyApiKey) {
    postMessageToParent({
      message: 'missing_company_key',
      id: ''
    });
    throw new Error('No company identifier');
  }

  useSuspenseApiQuery<Company>({
    queryOptions: {
      queryKey: ['companies', companyApiKey],
      retry: false
    },
    requestArguments: {
      method: 'GET',
      path: generatePath('/companies/:apiKey', {apiKey: companyApiKey}),
      onRequestError: () =>
        postMessageToParent({
          message: 'invalid_company_key',
          id: companyApiKey
        })
    }
  });

  const value = useMemo(
    () => ({
      companyApiKey,
      extraData,
      companyCallbackUrl,
      isForPatient: isForPatient === '1',
      showQuestionnairesHeader: showQuestionnairesHeader === '1',
      enableAlternativeCameraModule: enableCamera === '1' || enableAlternativeCameraModule === '1',
      enableAlternativeCameraModuleAndroid:
        enableCameraForAndroid === '1' || enableAlternativeCameraModuleAndroid === '1',
      enableAnamnesis: enableAnamnesis === '1',
      enableExtendedInstructions: enableExtendedInstructions === '1',
      enableResult: isResultEnabled === '0' || enableResult === '0' ? false : true,
      macroscopicMedia: macroscopicMedia as MacroscopicMediaParam,
      enableQrUpload: enableQrUpload === '1',
      visitIdentifier
    }),
    []
  );

  return <ParamsContext.Provider value={value}>{children}</ParamsContext.Provider>;
}

export function useParamsContext() {
  return useContext(ParamsContext);
}
